import React, { useEffect, useRef, useState } from 'react';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { Box, Flex, FormControl, InputV2, TextV2, TextArea, CheckboxV2, SpacingStack } from '@withjoy/joykit';
import { DialogV2 } from '@withjoy/joykit';
import { ReactComponent as Camera } from '@assets/icons/camera2.svg';
import { useAddCashFundDialogController } from '../../AddCashFundDialog.controller';
import { EditCashFundDialogProps } from '../../types';
import { CashFundAmount } from '../CashFundAmount';
import { CashFundConfirmation } from './CashFundConfirmation';
import { StyledColumns } from './EditCashFundDialog.styles';
import { StyledSpacingStackPhoto, StyledPhoto, EditPhotoButton } from '../../AddCashFundDialog.styles';
import { CustomItemDataType } from '@graphql/generated';
import { DialogFooter } from '../../../RegistryDialog/DialogFooter';
import { useFilestack } from '@shared/utils/filestack';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { CashFundPaymentMethodV2 } from '../CashFundPaymentMethodV2';
import { pxToRem } from '@withjoy/joykit/theme';
import { Intent } from '@withjoy/joykit/components/ButtonV2/Button.types';
import { AppPaymentDisclaimerDialog } from '../CashFundPaymentMethodV2/components/AppPaymentToggle/components/AppPaymentDisclaimerDialog/AppPaymentDisclaimerDialog';

const DialogInternal: React.FC<EditCashFundDialogProps & { onComplete: () => void }> = props => {
  const {
    isNewCashFund,
    eventId,
    product,
    hideDialog,
    donationFundInfo,
    isCharityFund,
    isCatalogDialog,
    selectedCategory,
    addMethodType,
    itemPosition,
    registryCurrency,
    orders,
    handleOnRemove,
    telemCategoryOverride,
    onComplete,
  } = props;
  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();
  const { nameLabel, maxCharacters, descriptionLabel, descriptionPlaceholder, saveButton, cancelButton, newPaymentMethodWarning, deleteButton } = getCashFundDialogTranslations();

  const [updatedPhoto, setUpdatedPhoto] = useState<string | undefined>(product?.image);
  const [photoFilename, setPhotoFilename] = useState<string | undefined>(undefined);

  const { open: openFilepicker, getPhotoPreviewUrl } = useFilestack({
    containerId: eventId,
    transformations: {
      crop: {
        aspectRatio: 1 / 1,
        force: true,
      },
    },
    onUploadDone: async res => {
      const { filename, url } = res.filesUploaded[0];
      const photo = await getPhotoPreviewUrl({
        url,
      });

      setUpdatedPhoto(photo);
      setPhotoFilename(filename);
    },
    maxFiles: 1,
  });

  const {
    formik,
    setPaymentMethod,
    selectedPaymentMethod,
    paymentLink,
    getClickableLink,
    onSaveClick,
    handleCancel,
    handleDeleteRegistryItem,
    preventNonNumericalInput,
    updateDonationFundLoading,
    NOTE_MAX_CHARS,
    onQtyChangeEdit,
    onAmountChangeEdit,
    totalGoalErrorMessage,
    isPaymentMethodWarningShown,
    totalGoal,
    onAmountFocus,
    onQtyFocus,
    onNameFocus,
    onUsernameFocus,
    onDescriptionFocus,
    onPhotoClick,
    donationMethodTabs,
    isLinkToCharitySelected,
    isAnyAmountSelected,
    amountTabs,
    onHideGoalToggle,
    isGoalHidden,
    onHelpIconClick,
    showCatalogCompletionStep,
    createDonationFundFromExistingProductLoading,
    collectionMembers,
    getMustHaveTranslations,
    trackEnableMustHave,
    handleViewRegistry,
    onCloseDisclaimerDialog,
    isOpenDisclaimerDialog,
    disclaimerFormik,
  } = useAddCashFundDialogController({
    addMethodType,
    eventId,
    product,
    photoFilename,
    updatedPhoto,
    hideDialog,
    isEditDialog: true,
    openFilepicker,
    donationFundInfo,
    isCharityFund,
    isCatalogDialog,
    isNewCashFund,
    selectedCategory,
    itemPosition,
    registryCurrency,
    handleOnRemove,
    fundType: isCharityFund ? CustomItemDataType.charity : CustomItemDataType.cash,
    telemCategoryOverride,
  });
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const { errors } = formik || {};
  const formikValues = formik.values;
  const descriptionLength = formikValues.description ? formikValues.description.length : 0;
  const currency = formikValues.currency || '';

  const { markGiftAs, mostWanted, mostWantedDescription } = getMustHaveTranslations();

  const buttonProps = {
    isLoading: updateDonationFundLoading || createDonationFundFromExistingProductLoading,
    primaryButton: saveButton,
    secondaryButton: cancelButton,
    handlePrimary: onSaveClick,
    handleSecondary: handleCancel,
    hideDivider: true,
    paddingX: '0px',
    ...(!(orders?.length || isNewCashFund) && {
      tertiaryButton: deleteButton,
      handleTertiary: handleDeleteRegistryItem,
    }),
    primaryButtonProps: { intent: 'neutral' as Intent },
  };

  useEffect(() => {
    if (isMobile && showCatalogCompletionStep) {
      onComplete();
    }
  }, [isMobile, showCatalogCompletionStep, onComplete]);

  return (
    <>
      <DialogV2.Header>
        <DialogV2.CloseButton onClick={handleCancel} />
      </DialogV2.Header>
      <DialogV2.Body
        maxHeight={['unset', '652px', '652px']}
        overflowX="hidden"
        overflowY="auto"
      >
        {showCatalogCompletionStep ? (
          <CashFundConfirmation
            handleViewRegistry={handleViewRegistry}
            cashFundTitle={formik.values.name}
            photoUrl={updatedPhoto}
            collectionMembers={collectionMembers}
            selectedCategory={selectedCategory}
          />
        ) : (
          <Box>
            <StyledColumns
              fluid={true}
              marginBottom="8px"
            >
              <StyledColumns.Column marginBottom={[4, 4, 0]}>
                <StyledPhoto
                  $backgroundImage={updatedPhoto}
                  backgroundColor={'mono2'}
                  width={328}
                  height={328}
                  marginX="auto"
                  onClick={onPhotoClick}
                >
                  <StyledSpacingStackPhoto
                    spacing={5}
                    tabIndex={0}
                  >
                    <EditPhotoButton>
                      <Camera />
                    </EditPhotoButton>
                  </StyledSpacingStackPhoto>
                </StyledPhoto>
              </StyledColumns.Column>
              <StyledColumns.Column>
                <form>
                  <Flex
                    flexDirection="column"
                    justifyContent="center"
                  >
                    <FormControl
                      label={
                        <TextV2
                          as="span"
                          typographyVariant="hed2"
                        >
                          {nameLabel}
                        </TextV2>
                      }
                      error={errors.name ? errors.name : undefined}
                      marginBottom={6}
                    >
                      <InputV2
                        {...formik.getFieldProps('name')}
                        tabIndex={0}
                        onFocus={onNameFocus}
                      />
                    </FormControl>
                    <CashFundAmount
                      isAnyAmountSelected={isAnyAmountSelected}
                      amountTabs={amountTabs}
                      formik={formik}
                      isMobile={!!isMobile}
                      onQtyChange={onQtyChangeEdit}
                      onQtyFocus={onQtyFocus}
                      preventNonNumericalInput={preventNonNumericalInput}
                      onAmountChange={onAmountChangeEdit}
                      onAmountFocus={onAmountFocus}
                      totalGoal={totalGoal}
                      currency={currency}
                      onHideGoalToggle={onHideGoalToggle}
                      isGoalHidden={isGoalHidden}
                      onHelpIconClick={onHelpIconClick}
                      isEditDialog
                    />
                    {totalGoalErrorMessage && !(errors.amount || errors.qty) ? (
                      <TextV2
                        typographyVariant="label2"
                        component="p"
                        color="negative6"
                        marginBottom={4}
                        marginTop={4}
                      >
                        {totalGoalErrorMessage}
                      </TextV2>
                    ) : null}
                    <SpacingStack
                      spacing={5}
                      marginY={5}
                    >
                      <TextV2
                        typographyVariant="hed2"
                        color="black"
                      >
                        {markGiftAs}
                      </TextV2>
                      <Box>
                        <CheckboxV2
                          marginBottom={pxToRem(6)}
                          {...formik.getFieldProps('mustHave')}
                          isChecked={formik.getFieldProps('mustHave').value}
                          onChange={event => {
                            formik.getFieldProps('mustHave').onChange(event);
                            trackEnableMustHave(product, event.target.checked);
                          }}
                        >
                          {mostWanted}
                        </CheckboxV2>
                        <TextV2
                          typographyVariant="label1"
                          color="mono10"
                          marginLeft={pxToRem(28)}
                        >
                          {mostWantedDescription}
                        </TextV2>
                      </Box>
                    </SpacingStack>
                    <Box
                      width="100%"
                      height="1px"
                      backgroundColor="mono3"
                      marginY={5}
                    />
                    <CashFundPaymentMethodV2
                      isCharityFund={isCharityFund}
                      isLinkToCharitySelected={isLinkToCharitySelected}
                      isMobile={!!isMobile}
                      donationMethodTabs={donationMethodTabs}
                      formik={formik}
                      setPaymentMethod={setPaymentMethod}
                      selectedPaymentMethod={selectedPaymentMethod}
                      paymentLink={paymentLink}
                      onUsernameFocus={onUsernameFocus}
                      isEditDialog
                      getClickableLink={getClickableLink}
                    />
                    <Box
                      width="100%"
                      height="1px"
                      backgroundColor="mono3"
                      marginY={5}
                    />
                    {isPaymentMethodWarningShown && !isLinkToCharitySelected ? (
                      <TextV2
                        typographyVariant="label2"
                        color="negative6"
                        component="p"
                        marginTop={4}
                      >
                        {newPaymentMethodWarning}
                      </TextV2>
                    ) : null}
                    <FormControl
                      label={
                        <TextV2
                          as="span"
                          typographyVariant="hed2"
                        >
                          {descriptionLabel}
                        </TextV2>
                      }
                      error={errors.description}
                      isInvalid={!!errors.description}
                      marginTop={5}
                    >
                      <>
                        <TextArea
                          {...formik.getFieldProps('description')}
                          placeholder={descriptionPlaceholder}
                          resize={false}
                          minRows={4}
                          maxRows={4}
                          tabIndex={0}
                          error={!!errors.description}
                          onFocus={onDescriptionFocus}
                        />
                        <TextV2
                          component="p"
                          typographyVariant="label2"
                          color={descriptionLength > NOTE_MAX_CHARS ? 'negative6' : 'mono10'}
                          textAlign="end"
                        >
                          {`${descriptionLength}/${NOTE_MAX_CHARS} ${maxCharacters}`}
                        </TextV2>
                      </>
                    </FormControl>
                  </Flex>
                </form>
              </StyledColumns.Column>
            </StyledColumns>
            <DialogFooter {...buttonProps} />
          </Box>
        )}
      </DialogV2.Body>
      <AppPaymentDisclaimerDialog
        isOpen={isOpenDisclaimerDialog}
        paymentLink={paymentLink}
        paymentUsername={formik.values.username}
        paymentMethod={formik.values.paymentMethod}
        onClose={onCloseDisclaimerDialog}
        formik={disclaimerFormik}
        currencyCode={formik.values.currencyCode}
      />
    </>
  );
};

export const EditCashFundDialog: React.FC<EditCashFundDialogProps> = props => {
  const { isOpen, hideDialog } = props;

  const dialogRef = useRef<HTMLDivElement>(null);

  const scrollDialogToTop = () => {
    if (dialogRef.current) {
      // Target dialog container is 3 levels up from the `DialogV2`
      dialogRef.current.parentElement?.parentElement?.parentElement?.scrollTo?.(0, 0);
    }
  };

  return (
    <DialogV2
      ref={dialogRef}
      isOpen={isOpen}
      id="EditCashFundDialog"
      onClose={hideDialog}
      size="3xl"
      disableFocusLock={true}
    >
      <DialogInternal
        {...props}
        onComplete={scrollDialogToTop}
      />
    </DialogV2>
  );
};

EditCashFundDialog.displayName = 'EditCashFundDialog';
